import { Grid } from '~/engine/data/common/Grid';
import { RoiReadModel } from '../readmodels/StructureSetReadModel';
import { ReversibleOperation, RoiMementoFactory } from './';
import { UndoRedoManager } from './UndoRedoManager';

export type ContourOperationResult = {
    compressedPixels: Uint8Array;
    grid: Grid;
    roiNumber: number;
};

/**
 * Service for contour undo/redo operations using full pixel snapshots.
 * Stores complete compressed pixel data for each operation.
 */
export class ContourUndoService {
    private roiStacks = new Map<number, UndoRedoManager>();
    private globalListeners = new Set<() => void>();

    private notifyGlobalListeners(): void {
        this.globalListeners.forEach((listener) => listener());
    }

    private getOrCreateStack(roiNumber: number): UndoRedoManager {
        if (!this.roiStacks.has(roiNumber)) {
            const stack = new UndoRedoManager();
            stack.subscribe(() => this.notifyGlobalListeners());
            this.roiStacks.set(roiNumber, stack);
        }
        return this.roiStacks.get(roiNumber)!;
    }

    private extractOperationResult(
        operation: ReversibleOperation
    ): ContourOperationResult | undefined {
        const state = operation.getState();
        if (!state.compressedContourPixels || !state.grid) {
            return undefined;
        }
        return {
            compressedPixels: state.compressedContourPixels,
            grid: state.grid,
            roiNumber: operation.getRoiNumber(),
        };
    }

    /**
     * For React hook to subscribe to global undo/redo state changes
     */
    subscribe(listener: () => void): { unsubscribe: () => void } {
        this.globalListeners.add(listener);
        listener(); // Call immediately
        return {
            unsubscribe: () => {
                this.globalListeners.delete(listener);
            },
        };
    }

    /**
     * Add a contour operation to the ROI's undo stack
     * @param operation - Operation name, use 'initial-state' for the first capture
     */
    async addContourOperation(
        roiReadModel: RoiReadModel,
        pixels: Uint8Array[],
        grid: Grid,
        operation: string
    ): Promise<void> {
        const description =
            operation === 'initial-state'
                ? `Initial state for ROI ${roiReadModel.roiNumber} (${roiReadModel.name})`
                : `${operation} on ROI ${roiReadModel.roiNumber} (${roiReadModel.name})`;

        const memento = await RoiMementoFactory.fromReadModelWithContours(
            roiReadModel,
            pixels,
            grid
        );

        const reversibleOp = new ReversibleOperation(memento, description);
        const stack = this.getOrCreateStack(roiReadModel.roiNumber);
        stack.addOperation(reversibleOp);
    }

    /**
     * Undo the last contour operation for a specific ROI
     */
    undo(roiNumber: number): ContourOperationResult | undefined {
        const stack = this.roiStacks.get(roiNumber);
        if (!stack) {
            return undefined;
        }

        const operation = stack.undo();
        if (!operation) {
            return undefined;
        }

        return this.extractOperationResult(operation);
    }

    /**
     * Redo the last undone contour operation for a specific ROI
     */
    redo(roiNumber: number): ContourOperationResult | undefined {
        const stack = this.roiStacks.get(roiNumber);
        if (!stack) {
            return undefined;
        }

        const operation = stack.redo();
        if (!operation) {
            return undefined;
        }

        return this.extractOperationResult(operation);
    }

    canUndo(roiNumber: number): boolean {
        const stack = this.roiStacks.get(roiNumber);
        return stack ? stack.canUndo() : false;
    }

    canRedo(roiNumber: number): boolean {
        const stack = this.roiStacks.get(roiNumber);
        return stack ? stack.canRedo() : false;
    }

    clear(): void {
        this.roiStacks.forEach((stack) => stack.clear());
        this.roiStacks.clear();
        this.notifyGlobalListeners();
    }

    isRoiInitialized(roiNumber: number): boolean {
        const stack = this.roiStacks.get(roiNumber);
        return stack ? stack.size > 0 : false;
    }
}
